import React, { useState, useEffect } from 'react';
import { Table, Spin, Tag, Descriptions, Button, Form, Space, Input, Upload, Modal, Divider, Tabs } from 'antd';
import type { TabsProps } from 'antd';
import { UnorderedListOutlined, AuditOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { InboxOutlined } from '@ant-design/icons';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import axios from 'axios';
import SweetAlert from 'sweetalert2';
import { useAuthHeader, useAuthUser } from '../../../../Services/Auth/sessionAuth';
import Utilisateur from '../../../../Models/Utilisateur';
import Data from '../../../../Data/Data.json';
import { Crypter } from '../../../../Functions/Security/Crypter';
import { Box, Typography } from '@mui/material';
import SearchModal from '../../../SearchModal';
import Params from '../../../../Types/ParamsPicklist';
import formatNumber from '../../../../Functions/Traitement/FormatNumber';

/**
 * Restructuration — Décision de l'organe compétent :
 * liste des dossiers EN ATTENTE / DECIDE / SIGNE,
 * actions : Approuver (scénario), Renvoyer en révision, Rejeter,
 * Signer l'avenant, Appliquer.
 */
const DecisionRestructuration = () => {
    const auth = useAuthUser();
    const user: Utilisateur = JSON.parse(JSON.stringify(auth()));
    const authHeader = useAuthHeader();

    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [cdEtat, setCdEtat] = useState<string>('EN ATTENTE');
    const [dossiers, setDossiers] = useState<any[]>([]);
    const [selectedKey, setSelectedKey] = useState<React.Key | null>(null);
    const [detail, setDetail] = useState<any>(null);       // {dossier, scenarios, docs, probation, echeances}
    const [scenChoice, setScenChoice] = useState<React.Key | null>(null);
    const [docs, setDocs] = useState<any[]>([]);
    const [avenantFile, setAvenantFile] = useState<File | null>(null);

    let searchMotifRej: Params = { CodeAgence: '', Table: "v_motif_restru_rejet", Colonne1: "CODMOTIF", Colonne2: "LIBMOTIF", Colagence: '101', Choix: 22 };

    useEffect(() => { loadListe(); }, [cdEtat]);

    function callApi(action: string, extra: any = {}, onSuccess?: (res: any) => void, silent = false) {
        setLoading(true);
        axios.request({
            method: 'post',
            maxBodyLength: Infinity,
            url: Data.ApiUrl.RestructurePret,
            headers: { 'Authorization': authHeader(), 'Content-Type': 'application/json' },
            data: Crypter.encrypt(JSON.stringify({
                action, utiVal: user.coduti, codAge: user.codage, ...extra
            }))
        })
            .then((response) => {
                setLoading(false);
                if (!response.data) return;
                const res = JSON.parse(Crypter.decrypt(response.data)).Value;
                if (res.StatutCode == 200) {
                    if (onSuccess) onSuccess(res);
                    else if (!silent) SweetAlert.fire({ title: res.Message, icon: "success" });
                } else if (!silent) {
                    SweetAlert.fire({ title: res.Message, icon: "warning" });
                }
            })
            .catch((error) => {
                setLoading(false);
                SweetAlert.fire({ title: String(error), icon: "error" });
            });
    }

    function loadListe() {
        callApi('LISTE', { cdEtat }, (res) => {
            setDossiers(res.Result?.Table ?? []);
            setDetail(null); setSelectedKey(null); setScenChoice(null);
        }, true);
    }

    function loadDetail(id: React.Key) {
        const d = dossiers.find(x => x.Id === id);
        callApi('DETAIL', { idRestru: id, numDos: d?.NUMDOS }, (res) => {
            const t = res.Result ?? {};
            const tables = Object.values(t).filter(Array.isArray) as any[][];
            setDetail({
                dossier: tables[0]?.[0] ?? null,
                scenarios: tables[1] ?? [],
                docs: tables[2] ?? [],
                probation: tables[3] ?? [],
                echeances: tables[4] ?? []
            });
            const choisi = (tables[1] ?? []).find((s: any) => s.ISCHOISI === 1 || s.ISCHOISI === true);
            setScenChoice(choisi?.Id ?? null);
            callApi('LISTEDOCS', { idRestru: id }, (r) => setDocs(r.Result?.Table ?? []), true);
        }, true);
    }

    // ---------------- Fiche de restructuration (PDF) ----------------
    // jsPDF ne rend pas l'espace fine U+202F de fr-FR -> espace normale comme séparateur de milliers
    const fmtM = (v: any) => new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
        .format(Number(v ?? 0)).replace(/[\u202F\u00A0]/g, ' ');
    const fmtD = (v: any) => v ? new Date(String(v).substring(0, 10)).toLocaleDateString('fr-FR') : '—';
    const periodLib: Record<string, string> = { J: 'Journalière', H: 'Hebdomadaire', F: 'Bimensuelle', M: 'Mensuelle', B: 'Bimestrielle', T: 'Trimestrielle', S: 'Semestrielle', A: 'Annuelle' };
    const typaLib: Record<string, string> = { C: 'Amortissement constant', V: 'Annuité constante', I: 'In fine' };

    function generateFiche() {
        const dos2: any = detail?.dossier;
        const scen: any = (detail?.scenarios ?? []).find((s: any) => s.Id === scenChoice)
            ?? (detail?.scenarios ?? []).find((s: any) => s.ISCHOISI === 1 || s.ISCHOISI === true)
            ?? (detail?.scenarios ?? [])[0];
        if (!dos2 || !scen) { SweetAlert.fire({ title: "Aucun scénario à imprimer", icon: "warning" }); return; }

        const prob: any = (detail?.probation ?? [])[0];
        const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
        const L = 40, W = 515;
        const today = new Date().toLocaleDateString('fr-FR');

        // ---- En-tête ----
        doc.setFillColor(17, 101, 153);
        doc.rect(0, 0, 595, 70, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16); doc.setFont('helvetica', 'bold');
        doc.text('FICHE DE RESTRUCTURATION DE PRÊT', 297, 30, { align: 'center' });
        doc.setFontSize(10); doc.setFont('helvetica', 'normal');
        doc.text(`Réf. ${dos2.NUMDOS}/RESTRU-${dos2.Id}  •  Agence ${dos2.AGCCPT ?? user.codage ?? ''}  •  Émise le ${today}`, 297, 50, { align: 'center' });
        doc.setTextColor(0, 0, 0);

        // ---- 1. Prêt concerné ----
        autoTable(doc, {
            head: [['1. Prêt concerné', '']],
            body: [
                ['Client', `${dos2.NOMCLI ?? ''} (${dos2.NUMCLI ?? ''})`],
                ['Type de prêt', `${dos2.TYPEPRET ?? ''} — inchangé (pas de novation)`],
                ['Montant initial', `${fmtM(dos2.mntOrig)}`],
                ['Taux d\'intérêt actuel', `${dos2.tauxOrig ?? '—'} %`],
                ['Taux de pénalité actuel', `${dos2.tauxpenOrig ?? '—'} %`],
                ['Amortissement actuel', typaLib[dos2.typamortOrig] ?? dos2.typamortOrig ?? '—'],
                ['Périodicité actuelle', periodLib[dos2.periodicOrig] ?? dos2.periodicOrig ?? '—'],
                ['Échéances restantes (initiales)', `${dos2.nbrEcheOrig ?? '—'}`],
            ],
            startY: 88, styles: { fontSize: 9 }, columnStyles: { 0: { fontStyle: 'bold', cellWidth: 200 } },
            headStyles: { fillColor: [17, 101, 153], textColor: 255 }, theme: 'grid', margin: { left: L, right: L },
        });

        // ---- 2. Dossier de restructuration ----
        autoTable(doc, {
            head: [['2. Dossier de restructuration', '']],
            body: [
                ['Motif', `${dos2.MOTIF ?? '—'}`],
                ['Diagnostic financier', `${dos2.DIAGNOSTIC ?? '—'}`],
                ['Commentaire', `${dos2.COMMENTAIRE ?? '—'}`],
                ['Saisi par / le', `${dos2.UTILIS ?? '—'}  /  ${fmtD(dos2.DATCRE)}`],
                ['Décidé par / le', `${dos2.UTIVAL ?? '—'}  /  ${fmtD(dos2.DATVAL)}`],
            ],
            startY: (doc as any).lastAutoTable.finalY + 10, styles: { fontSize: 9 },
            columnStyles: { 0: { fontStyle: 'bold', cellWidth: 200 } },
            headStyles: { fillColor: [17, 101, 153], textColor: 255 }, theme: 'grid', margin: { left: L, right: L },
        });

        // ---- 3. Scénario retenu ----
        autoTable(doc, {
            head: [['3. Scénario retenu', '']],
            body: [
                ['Scénario', `${scen.LIBSCEN ?? ''} (#${scen.Id})`],
                ['Nouveau capital', `${fmtM(scen.MNTCAPIT)}`],
                ['Remise décondonnée', `${fmtM(scen.MNTDECONDONNE)}`],
                ['Intérêts capitalisés', `${fmtM(scen.INTIMPAYE)}`],
                ['Pénalités capitalisées', `${fmtM(scen.PENIMPAYE)}`],
                ['Nombre d\'échéances', `${scen.NBRECHE ?? '—'}`],
                ['Périodicité', periodLib[scen.PERIODIC] ?? scen.PERIODIC ?? '—'],
                ['Première échéance', fmtD(scen.DAT1ECHE)],
                ['Taux d\'intérêt négocié', scen.TAUXINT != null ? `${scen.TAUXINT} %` : `${dos2.tauxOrig} % (inchangé)`],
                ['Taux de pénalité négocié', scen.TAUXPEN != null ? `${scen.TAUXPEN} %` : `${dos2.tauxpenOrig} % (inchangé)`],
                ['Type d\'amortissement', typaLib[scen.TYPAMORT] ?? `${typaLib[dos2.typamortOrig] ?? dos2.typamortOrig} (inchangé)`],
                ['Frais de restructuration', `${fmtM(scen.MTNFRAIS)}${scen.CODOPE ? ` (${scen.CODOPE})` : ''}`],
            ],
            startY: (doc as any).lastAutoTable.finalY + 10, styles: { fontSize: 9 },
            columnStyles: { 0: { fontStyle: 'bold', cellWidth: 200 } },
            headStyles: { fillColor: [17, 101, 153], textColor: 255 }, theme: 'grid', margin: { left: L, right: L },
        });

        // ---- 4. Période probatoire COBAC ----
        if (prob) {
            autoTable(doc, {
                head: [['4. Période probatoire (COBAC)', '']],
                body: [
                    ['Début', fmtD(prob.DATDEBUT)],
                    ['Fin prévue', fmtD(prob.DATFINPREV)],
                    ['Fonds propres min. à rembourser', `${fmtM(prob.MNTMINFONDSPRO)}`],
                    ['Intérêts arriérés', `${fmtM(prob.INTARRIERES)}`],
                    ['Créance arrêtée', `${fmtM(prob.CREANCE)}`],
                ],
                startY: (doc as any).lastAutoTable.finalY + 10, styles: { fontSize: 9 },
                columnStyles: { 0: { fontStyle: 'bold', cellWidth: 200 } },
                headStyles: { fillColor: [17, 101, 153], textColor: 255 }, theme: 'grid', margin: { left: L, right: L },
            });
        }

        // ---- 5. Échéancier (aperçu) ----
        const echs: any[] = detail?.echeances ?? [];
        if (echs.length > 0) {
            autoTable(doc, {
                head: [['N°', 'Date', 'Capital', 'Amortissement', 'Intérêt', 'Taxe', 'Annuité TTC']],
                body: echs.slice(0, 36).map(e => [
                    e.numSeq, e.dateEcheacnce, fmtM(e.capital), fmtM(e.amortissement),
                    fmtM(e.interet), fmtM(e.taxe), fmtM(e.anTTC)
                ]),
                startY: (doc as any).lastAutoTable.finalY + 10, styles: { fontSize: 8 },
                headStyles: { fillColor: [17, 101, 153], textColor: 255 }, theme: 'grid', margin: { left: L, right: L },
                foot: echs.length > 36 ? [[`… ${echs.length - 36} autres échéances`, '', '', '', '', '', '']] : undefined,
            });
        }

        // ---- Signatures ----
        let y = (doc as any).lastAutoTable.finalY + 30;
        if (y > 720) { doc.addPage(); y = 60; }
        doc.setFontSize(10);
        doc.text('Fait à _________________________, le ____ / ____ / ________', L, y);
        doc.setFontSize(9);
        doc.text('Le client\n(signature et cachet)', L + 70, y + 25);
        doc.text('Le gestionnaire\n(signature et cachet)', L + 230, y + 25);
        doc.text('L\'organe de décision\n(signature et cachet)', L + 390, y + 25);
        doc.rect(L + 30, y + 55, 140, 80);
        doc.rect(L + 200, y + 55, 140, 80);
        doc.rect(L + 370, y + 55, 140, 80);

        doc.save(`Fiche_Restructuration_${dos2.NUMDOS}.pdf`);
    }

    // ---------------- Pièces : téléchargement + visualisation ----------------
    const [viewer, setViewer] = useState<{ open: boolean; url: string; mime: string; name: string }>(
        { open: false, url: '', mime: '', name: '' });

    // base64 -> Blob (détecte PDF / PNG / JPEG via les magic bytes)
    function base64ToBlob(b64: string): { blob: Blob; mime: string } {
        const bin = atob(b64.replace(/\s/g, ''));
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        let mime = 'application/octet-stream';
        if (bin.startsWith('%PDF')) mime = 'application/pdf';
        else if (bytes[0] === 0x89 && bytes[1] === 0x50) mime = 'image/png';
        else if (bytes[0] === 0xFF && bytes[1] === 0xD8) mime = 'image/jpeg';
        return { blob: new Blob([bytes], { type: mime }), mime };
    }

    function fetchDoc(idDoc: number, name: string, mode: 'download' | 'view') {
        callApi('GETDOC', { idScen: idDoc }, (res) => {
            const d = res.Result?.Table?.[0];
            if (!d?.PIECE) { SweetAlert.fire({ title: "Document vide", icon: "warning" }); return; }
            const { blob, mime } = base64ToBlob(d.PIECE);
            const url = URL.createObjectURL(blob);
            if (mode === 'download') {
                const a = document.createElement('a');
                a.href = url;
                a.download = name ?? d.LIBDOC ?? `doc_${idDoc}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 4000);
            } else {
                setViewer({ open: true, url, mime, name: name ?? d.LIBDOC ?? `doc_${idDoc}` });
            }
        }, true);
    }

    const cols: ColumnsType<any> = [
        { title: 'Dossier', dataIndex: 'NUMDOS', width: 110 },
        { title: 'Client', dataIndex: 'NOMCLI' },
        { title: 'Type', dataIndex: 'TYPEPRET', width: 60 },
        { title: 'Motif', dataIndex: 'MOTIF', width: 80 },
        { title: 'Nouv. capital', dataIndex: 'MNTCAPIT', width: 110, render: (v: number) => v != null ? formatNumber(v) : '—' },
        { title: 'État', dataIndex: 'CDETAT', width: 100, render: (v: string) => <Tag color="gold">{v}</Tag> },
        { title: 'Saisi le', dataIndex: 'DATCRE', width: 105, render: (v: any) => v ? String(v).substring(0, 10) : '' },
    ];

    const colsScen: ColumnsType<any> = [
        { title: 'Id', dataIndex: 'Id', width: 50 },
        { title: 'Scénario', dataIndex: 'LIBSCEN' },
        { title: 'Nb éch.', dataIndex: 'NBRECHE', width: 70 },
        { title: 'Taux', dataIndex: 'TAUXINT', width: 70, render: (v: any) => v ?? '—' },
        { title: 'Nouv. capital', dataIndex: 'MNTCAPIT', render: (v: number) => formatNumber(v) },
        { title: 'Frais', dataIndex: 'MTNFRAIS', width: 80, render: (v: number) => formatNumber(v ?? 0) },
        { title: 'Retenu', dataIndex: 'ISCHOISI', width: 80, render: (v: any) => (v === 1 || v === true) ? <Tag color="green">Oui</Tag> : '' },
    ];

    const colsEch: ColumnsType<any> = [
        { title: 'N°', dataIndex: 'numSeq', width: 55 },
        { title: 'Date', dataIndex: 'dateEcheacnce', width: 105 },
        { title: 'Capital', dataIndex: 'capital', render: (v: number) => formatNumber(v) },
        { title: 'Amort.', dataIndex: 'amortissement', render: (v: number) => formatNumber(v) },
        { title: 'Intérêt', dataIndex: 'interet', render: (v: number) => formatNumber(v) },
        { title: 'Annuité TTC', dataIndex: 'anTTC', render: (v: number) => formatNumber(v) },
    ];

    const dos: any = detail?.dossier;
    const etat: string = dos?.CDETAT ?? '';

    return (
        <div className='tab'>
            <Typography align='center' className='formTitle'><b>Décision de restructuration</b></Typography>
            <Spin spinning={loading} size='large' style={{ color: '#116599' }}>
                <Form form={form} layout="vertical" labelAlign='left'>
                    <div className='bG'>
                        <legend><Typography color={"#116599"}>Actions</Typography></legend>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Button size='large' className='bt_add' onClick={loadListe} type="ghost" htmlType="button" block>Actualiser</Button>
                            <Divider plain style={{ margin: '4px 0', borderColor: '#116599' }}>Décision</Divider>
                            <Button size='large' className='bt_add' disabled={etat !== 'EN ATTENTE'} onClick={() => {
                                if (!scenChoice) { SweetAlert.fire({ title: "Sélectionnez le scénario retenu", icon: "warning" }); return; }
                                SweetAlert.fire({
                                    title: `Approuver le dossier ${dos?.NUMDOS} ?`,
                                    text: `Scénario retenu : ${scenChoice}. Le dossier passera à DECIDÉ.`,
                                    showCancelButton: true, confirmButtonText: "Approuver", cancelButtonText: "Non",
                                    confirmButtonColor: "#116599", icon: 'question'
                                }).then((r) => {
                                    if (r.isConfirmed)
                                        callApi('DECIDER', { idRestru: dos?.Id, numDos: dos?.NUMDOS, idScen: scenChoice, decision: 'APP' },
                                            (res) => { SweetAlert.fire({ title: res.Message, icon: "success" }); loadListe(); });
                                });
                            }} type="ghost" htmlType="button" block>Approuver le scénario</Button>
                            <Button size='large' className='bt_add' disabled={etat !== 'EN ATTENTE'} onClick={() => {
                                SweetAlert.fire({
                                    title: `Renvoyer le dossier ${dos?.NUMDOS} en révision ?`,
                                    input: 'text', inputPlaceholder: 'Commentaire pour le gestionnaire',
                                    showCancelButton: true, confirmButtonText: "Renvoyer", cancelButtonText: "Non",
                                    confirmButtonColor: "#e2a900", icon: 'question'
                                }).then((r) => {
                                    if (r.isConfirmed)
                                        callApi('DECIDER', { idRestru: dos?.Id, numDos: dos?.NUMDOS, decision: 'REV', coment: r.value },
                                            (res) => { SweetAlert.fire({ title: res.Message, icon: "success" }); loadListe(); });
                                });
                            }} type="ghost" htmlType="button" block>Renvoyer en révision</Button>
                            <Button size='large' className='bt_cancel' style={{ color: '#fff' }} disabled={etat !== 'EN ATTENTE'} onClick={() => {
                                if (!form.getFieldValue('MOTIFREJ')) { SweetAlert.fire({ title: "Sélectionnez un motif de rejet", icon: "warning" }); return; }
                                SweetAlert.fire({
                                    title: `Rejeter le dossier ${dos?.NUMDOS} ?`,
                                    text: `Motif : ${form.getFieldValue('MOTIFREJ')}${form.getFieldValue('COMREJET') ? ' — ' + form.getFieldValue('COMREJET') : ''}`,
                                    showCancelButton: true, confirmButtonText: "Rejeter", cancelButtonText: "Non",
                                    confirmButtonColor: "#a41b1b", icon: 'warning'
                                }).then((r) => {
                                    if (r.isConfirmed)
                                        callApi('DECIDER', {
                                            idRestru: dos?.Id, numDos: dos?.NUMDOS, decision: 'REJ',
                                            motifRej: form.getFieldValue('MOTIFREJ'),
                                            coment: form.getFieldValue('COMREJET')
                                        }, (res) => { SweetAlert.fire({ title: res.Message, icon: "success" }); loadListe(); });
                                });
                            }} type="ghost" htmlType="button" block>Rejeter</Button>
                            <Divider plain style={{ margin: '4px 0', borderColor: '#116599' }}>Signature & application</Divider>
                            <Button size='large' className='bt_add' disabled={etat !== 'DECIDE' && etat !== 'SIGNE' && etat !== 'PROBATOIRE' && etat !== 'RECLASSE'} onClick={generateFiche} type="ghost" htmlType="button" block>Télécharger la fiche</Button>
                            <Button size='large' className='bt_add' disabled={etat !== 'DECIDE' || !avenantFile} onClick={() => {
                                if (!avenantFile) { SweetAlert.fire({ title: "Sélectionnez l'avenant signé (une seule fois)", icon: "warning" }); return; }
                                const reader = new FileReader();
                                reader.onload = () => {
                                    const b64 = String(reader.result ?? '').split(',')[1] ?? '';
                                    // 1. Upload de l'avenant signe, 2. passage en SIGNE
                                    callApi('UPLOADDOC', {
                                        idRestru: dos?.Id, numDos: dos?.NUMDOS,
                                        typDoc: 'AVENANT', libDoc: avenantFile.name,
                                        refDoc: form.getFieldValue('REFDOC') ?? avenantFile.name,
                                        piece: b64
                                    }, () => {
                                        callApi('SIGNER', { idRestru: dos?.Id, numDos: dos?.NUMDOS, typDoc: 'AVENANT' },
                                            (res) => {
                                                SweetAlert.fire({ title: "Avenant signé enregistré", text: res.Message, icon: "success" });
                                                setAvenantFile(null);
                                                loadListe();
                                            });
                                    });
                                };
                                reader.readAsDataURL(avenantFile);
                            }} type="ghost" htmlType="button" block>Soumettre la signature</Button>
                            <Button size='large' className='bt_add' disabled={etat !== 'SIGNE' || !docs.some((d: any) => d.TYPDOC === 'AVENANT' && d.HASPIECE === 1)} onClick={() => {
                                SweetAlert.fire({
                                    title: `Appliquer la restructuration du prêt ${dos?.NUMDOS} ?`,
                                    text: "Les échéances non payées seront clôturées et le nouveau plan activé. La période probatoire COBAC démarre.",
                                    showCancelButton: true, confirmButtonText: "Appliquer", cancelButtonText: "Non",
                                    confirmButtonColor: "#116599", icon: 'question'
                                }).then((r) => {
                                    if (r.isConfirmed)
                                        callApi('APPLIQUER', { idRestru: dos?.Id, numDos: dos?.NUMDOS },
                                            (res) => { SweetAlert.fire({ title: res.Message, icon: "success" }); loadListe(); });
                                });
                            }} type="ghost" htmlType="button" block>Appliquer</Button>
                        </Space>
                        {dos && (
                            <Tag color="gold" style={{ marginTop: 10, whiteSpace: 'normal' }}>
                                {dos.NUMDOS} — {etat}
                            </Tag>
                        )}
                    </div>
                    <div className='clientP'>
                        <div style={{ width: '88%' }}>
                            <Tabs size='small' type="card" defaultActiveKey="1" tabBarGutter={8}
                                tabBarStyle={{ overflowX: 'auto', whiteSpace: 'nowrap' }} items={[
                                    {
                                        key: '1',
                                        label: <span><UnorderedListOutlined /> Dossiers à traiter</span>,
                                        children: (
                                            <div className='tabItem' style={{ display: 'block', width: '100%' }}>
                                                <fieldset className="bordure1" style={{ marginBottom: 12, width: '100%' }}>
                                                    <legend style={{ fontSize: '15px', padding: '0 10px', color: '#116599' }}>
                                                        Dossiers
                                                        <Space style={{ marginLeft: 12 }}>
                                                            {['EN ATTENTE', 'DECIDE', 'SIGNE', 'REJETE', 'PROBATOIRE'].map(s => (
                                                                <Tag key={s} color={cdEtat === s ? '#116599' : 'default'} style={{ cursor: 'pointer' }}
                                                                    onClick={() => setCdEtat(s)}>{s}</Tag>
                                                            ))}
                                                        </Space>
                                                    </legend>
                                                    <Table size='small' columns={cols} dataSource={dossiers}
                                                        scroll={{ x: 700, y: 380 }} pagination={false} rowKey="Id"
                                                        rowSelection={{
                                                            type: 'radio', selectedRowKeys: selectedKey !== null ? [selectedKey] : [],
                                                            onChange: (keys) => { const k = keys[0] ?? null; setSelectedKey(k); if (k) loadDetail(k); }
                                                        }}
                                                        onRow={(r) => ({ onClick: () => { setSelectedKey(r.Id); loadDetail(r.Id); }, style: { cursor: 'pointer' } })} />
                                                </fieldset>
                                            </div>
                                        )
                                    },
                                    {
                                        key: '2',
                                        label: <span><AuditOutlined /> Détail & décision</span>,
                                        children: (
                                            <div className='tabItem' style={{ display: 'block', width: '100%' }}>
                            {detail && dos ? (
                                <fieldset className="bordure1" style={{ marginBottom: 12, width: '100%' }}>
                                    <legend style={{ fontSize: '15px', padding: '0 10px', color: '#116599' }}>Dossier {dos.NUMDOS} — {dos.NOMCLI}</legend>
                                    <Descriptions size="small" column={2} bordered style={{ marginBottom: 10 }}>
                                        <Descriptions.Item label="Type de prêt">{dos.TYPEPRET}</Descriptions.Item>
                                        <Descriptions.Item label="Statut">{dos.CDETAT}</Descriptions.Item>
                                        <Descriptions.Item label="Montant initial">{formatNumber(dos.mntOrig)}</Descriptions.Item>
                                        <Descriptions.Item label="Diagnostic">{dos.DIAGNOSTIC ?? '—'}</Descriptions.Item>
                                        <Descriptions.Item label="Décideur">{dos.UTIVAL ?? '—'}</Descriptions.Item>
                                        <Descriptions.Item label="Date décision">{dos.DATVAL ? String(dos.DATVAL).substring(0, 10) : '—'}</Descriptions.Item>
                                    </Descriptions>
                                    <Table size='small' columns={colsScen} dataSource={detail.scenarios} pagination={false} rowKey="Id"
                                        rowSelection={{
                                            type: 'radio', selectedRowKeys: scenChoice !== null ? [scenChoice] : [],
                                            onChange: (keys) => setScenChoice(keys[0] ?? null)
                                        }}
                                        onRow={(r) => ({ onClick: () => setScenChoice(r.Id), style: { cursor: 'pointer' } })} />
                                    {detail.echeances?.length > 0 && (
                                        <>
                                            <Typography color="#116599" style={{ margin: '10px 0 4px' }}><b>Échéancier du scénario retenu</b></Typography>
                                            <Table size='small' columns={colsEch} dataSource={detail.echeances}
                                                scroll={{ x: 700, y: 200 }} pagination={false} rowKey="numSeq" />
                                        </>
                                    )}
                                    {/* Pièces jointes (docs visibles à l'approbation) */}
                                    <Box sx={{ mt: 2 }}>
                                        <Typography color="#116599"><b>Pièces jointes du dossier</b></Typography>
                                        <Table size='small' dataSource={docs} pagination={false} rowKey="Id"
                                            columns={[
                                                { title: 'Type', dataIndex: 'TYPDOC', width: 100, render: (v: string) => <Tag>{v}</Tag> },
                                                { title: 'Document', dataIndex: 'LIBDOC' },
                                                { title: 'Réf.', dataIndex: 'REF', width: 110 },
                                                {
                                                    title: 'Pièce', dataIndex: 'HASPIECE', width: 180,
                                                    render: (v: any, d: any) => v === 1
                                                        ? <Space size={4}>
                                                            <Button size='small' type='link' onClick={() => fetchDoc(d.Id, d.LIBDOC, 'view')}>Visualiser</Button>
                                                            <Button size='small' type='link' onClick={() => fetchDoc(d.Id, d.LIBDOC, 'download')}>Télécharger</Button>
                                                        </Space>
                                                        : <Tag color="orange">Non jointe</Tag>
                                                },
                                                { title: 'Signé', dataIndex: 'ISSIGNE', width: 70, render: (v: any) => v === 1 ? <Tag color="green">Oui</Tag> : '' },
                                                { title: 'Déposé par', dataIndex: 'UTILIS', width: 90 },
                                                { title: 'Déposé le', dataIndex: 'DATCRE', width: 100, render: (v: any) => v ? String(v).substring(0, 10) : '' },
                                            ]} />
                                    </Box>

                                    {/* Annotations : qui a fait quoi */}
                                    <Box sx={{ mt: 2 }}>
                                        <Typography color="#116599"><b>Intervenants sur le dossier</b></Typography>
                                        <Descriptions size="small" column={2} bordered>
                                            <Descriptions.Item label="Dossier saisi par">{dos?.UTILIS ?? '—'}</Descriptions.Item>
                                            <Descriptions.Item label="Date de saisie">{fmtD(dos?.DATCRE)}</Descriptions.Item>
                                            <Descriptions.Item label="Décision par (organe)">{dos?.UTIVAL ?? '—'}</Descriptions.Item>
                                            <Descriptions.Item label="Date de décision">{fmtD(dos?.DATVAL)}</Descriptions.Item>
                                            <Descriptions.Item label="Avenant déposé par">{docs.find((d: any) => d.TYPDOC === 'AVENANT' && d.HASPIECE === 1)?.UTILIS ?? '—'}</Descriptions.Item>
                                            <Descriptions.Item label="Avenant déposé le">{fmtD(docs.find((d: any) => d.TYPDOC === 'AVENANT' && d.HASPIECE === 1)?.DATCRE)}</Descriptions.Item>
                                        </Descriptions>
                                    </Box>

                                    {/* Zone de rejet (dossier en attente de décision) */}
                                    {etat === 'EN ATTENTE' && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography color="#a41b1b"><b>Rejet du dossier</b></Typography>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
                                                <SearchModal width={9} form={form} Titre='Motif du rejet' label='Motif du rejet' name='MOTIFREJ' Parametre={searchMotifRej} />
                                                <Form.Item name="COMREJET" label="Commentaire du rejet">
                                                    <Input />
                                                </Form.Item>
                                            </Box>
                                        </Box>
                                    )}

                                    {/* Upload de l'avenant signé (après décision) */}
                                    {etat === 'DECIDE' && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography color="#116599"><b>Signature de l'avenant</b></Typography>
                                            <Typography style={{ fontSize: 12, marginBottom: 8 }}>
                                                1. Téléchargez la fiche de restructuration · 2. Faites-la signer par les parties prenantes
                                                · 3. Chargez le document signé (une seule fois) · 4. Soumettez la signature.
                                            </Typography>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
                                                <Form.Item name="REFDOC" label="Référence avenant">
                                                    <Input placeholder="Réf. document (optionnel)" />
                                                </Form.Item>
                                                <Upload.Dragger maxCount={1} beforeUpload={(file) => { setAvenantFile(file); return false; }}
                                                    onRemove={() => setAvenantFile(null)} accept=".pdf,.png,.jpg,.jpeg">
                                                    <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                                                    <p className="ant-upload-text">Glissez l'avenant signé ici ou cliquez</p>
                                                    <p className="ant-upload-hint">Un seul fichier — PDF ou image scannée</p>
                                                </Upload.Dragger>
                                            </Box>
                                        </Box>
                                    )}
                                </fieldset>
                            ) : (
                                <Tag color="orange" style={{ marginTop: 8 }}>Sélectionnez un dossier dans l'onglet « Dossiers à traiter ».</Tag>
                            )}
                                            </div>
                                        )
                                    }
                                ]} />
                        </div>
                    </div>
                </Form>
            </Spin>
            {/* Visionneuse de pièce */}
            <Modal open={viewer.open} title={viewer.name} footer={null} width={900}
                onCancel={() => { URL.revokeObjectURL(viewer.url); setViewer({ open: false, url: '', mime: '', name: '' }); }}>
                {viewer.mime === 'application/pdf' ? (
                    <iframe src={viewer.url} title={viewer.name} style={{ width: '100%', height: '70vh', border: 'none' }} />
                ) : viewer.mime.startsWith('image/') ? (
                    <img src={viewer.url} alt={viewer.name} style={{ maxWidth: '100%' }} />
                ) : (
                    <p>Format non visualisable — utilisez « Télécharger ».</p>
                )}
            </Modal>
        </div>
    );
};

export default DecisionRestructuration;
